import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';
import { AccountContribution, ClientAccountEntry } from '../../common/types/clients-entity';
import type { InvestmentSourcesType } from '../../common/types/clients-entity';
import { saveClient } from '../client';
import { CONTRIBUTION_FREQUENCY } from '../../common/contributionPeriodOptionRules';

// Standalone slice (parallel to `adviceClient`) backing the redesigned inline Accounts
// table (AEX-3198). It keeps the new inline-editable accounts + their nested contribution
// funding rows decoupled from the legacy `adviceClient.accounts` model. On save the accounts
// are copied into `adviceClient.accounts.investmentsSources` (contributions nested) and
// persisted via `updateClientProfile`; on load the slice is hydrated from the same
// `investmentsSources` array (AEX-3270).
export interface ClientAccountsState {
    accounts: ClientAccountEntry[];
}

const initialState: ClientAccountsState = {
    accounts: []
};

const clientAccounts = createSlice({
    name: 'adviceClientAccounts',
    initialState,
    reducers: {
        // Insert a brand new account or replace an existing one (matched by client-only localId).
        upsertClientAccount: (state, action: PayloadAction<ClientAccountEntry>) => {
            const incoming = action.payload;
            const index = state.accounts.findIndex((account) => account.localId === incoming.localId);
            if (index === -1) {
                state.accounts.push(incoming);
            } else {
                state.accounts[index] = incoming;
            }
        },
        deleteClientAccount: (state, action: PayloadAction<string>) => {
            state.accounts = state.accounts.filter((account) => account.localId !== action.payload);
        },
        setClientAccounts: (state, action: PayloadAction<ClientAccountEntry[]>) => {
            state.accounts = action.payload;
        },
        resetClientAccounts: () => initialState
    }
});

export const { upsertClientAccount, deleteClientAccount, setClientAccounts, resetClientAccounts } =
    clientAccounts.actions;

// ---------------------------------------------------------------------------
// Sync helpers: map the inline-table model to/from the persisted `investmentsSources`
// account entities (AEX-3270). `accountId` is only sent when the backend has already
// assigned one; new rows omit it so the backend can generate it. `localId` is client-only
// and stripped before persisting.
// ---------------------------------------------------------------------------

// Strip the client-only `localId` from a contribution while retaining every other field
// (including `accountId` when present) so it can be persisted verbatim. For "One Time"
// (onetime) contributions the '/updateclientprofile' payload sends the same date for
// beginningYear and endYear.
const stripContributionLocalId = (contribution: AccountContribution) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { localId, accountId, ...rest } = contribution;
    const normalized =
        rest.frequency === CONTRIBUTION_FREQUENCY.ONE_TIME ? { ...rest, endYear: rest.beginningYear } : rest;
    return { ...(accountId ? { accountId } : {}), ...normalized };
};

// Map an inline-table account (with its nested contribution funding rows) into a persisted
// `investmentsSources` entry. Contributions are carried as a nested array.
const mapAccountToInvestmentSource = (account: ClientAccountEntry): Partial<InvestmentSourcesType> => ({
    ...(account.accountId ? { accountId: account.accountId } : {}),
    accountName: account.accountName,
    accountType: account.accountType,
    ...(account.term ? { term: account.term } : {}),
    accountHolder: account.accountHolder,
    amount: Number(account.amount) || 0,
    contributions: (account.contributions ?? []).map(stripContributionLocalId)
});

// Reverse mapping: rebuild an inline-table account from a persisted `investmentsSources`
// entry, re-attaching the client-only `localId` to the account and each contribution.
const mapInvestmentSourceToAccount = (source: Partial<InvestmentSourcesType>): ClientAccountEntry => ({
    localId: source.accountId || uuidv4(),
    ...(source.accountId ? { accountId: source.accountId } : {}),
    accountType: source.accountType ?? '',
    ...(source.term ? { term: source.term } : {}),
    accountHolder: source.accountHolder ?? '',
    accountName: source.accountName ?? '',
    amount: typeof source.amount === 'number' ? source.amount : Number(source.amount) || 0,
    contributions: (source.contributions ?? []).map((contribution) => ({
        ...contribution,
        localId: contribution.accountId || uuidv4()
    }))
});

// Persist the inline-table state by copying `adviceClientAccounts.accounts` into
// `adviceClient.accounts.investmentsSources` (contributions nested) and calling
// `updateClientProfile` (via `saveClient`). `incomeSources` is left untouched. A no-op
// guard skips the round-trip when the mapped accounts are identical to what was last
// persisted. After the response, the inline-table slice is refilled from
// `adviceClient.accounts.investmentsSources`. Returns `true` when a change was persisted,
// `false` when the no-op guard short-circuited (nothing changed).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const persistClientAccounts =
    () =>
    async (dispatch: any, getState: any): Promise<boolean> => {
        const state = getState();
        const accounts: ClientAccountEntry[] = state.adviceClientAccounts.accounts;
        const adviceClient = state.adviceClient;

        const investmentsSources = accounts.map(mapAccountToInvestmentSource);
        const persistedInvestmentsSources: Partial<InvestmentSourcesType>[] =
            adviceClient?.accounts?.investmentsSources ?? [];

        // No-op guard: normalize the last-persisted `investmentsSources` through the same
        // projection (dropping client-only ids and any backend-only fields) so the comparison is
        // apples-to-apples, then skip the `updateClientProfile` round-trip when nothing changed.
        const persistedProjection = persistedInvestmentsSources
            .map(mapInvestmentSourceToAccount)
            .map(mapAccountToInvestmentSource);
        if (isEqual(investmentsSources, persistedProjection)) {
            return false;
        }

        const payload = {
            ...adviceClient,
            accounts: {
                ...adviceClient.accounts,
                investmentsSources
            }
        };

        await dispatch(saveClient(payload));

        // `saveClient` syncs the response into `adviceClient`; refill the inline table from the
        // freshly persisted `investmentsSources`.
        const responseInvestmentsSources: Partial<InvestmentSourcesType>[] =
            getState().adviceClient?.accounts?.investmentsSources ?? [];
        dispatch(setClientAccounts(responseInvestmentsSources.map(mapInvestmentSourceToAccount)));
        return true;
    };

export const updateAccountState =
    (updatedAccount: ClientAccountEntry) =>
    async (dispatch: any, getState: any): Promise<boolean> => {
        const state = getState();
        const accountsState: ClientAccountEntry[] = state.adviceClientAccounts.accounts;
        const adviceClient = state.adviceClient;
        // Upsert the incoming account into a local copy of the slice, then persist that projection.
        const LocalAccountsState: ClientAccountEntry[] = [...accountsState];

        const index = LocalAccountsState.findIndex((account) => account.localId === updatedAccount.localId);
        if (index === -1) {
            LocalAccountsState.push(updatedAccount);
        } else {
            LocalAccountsState[index] = updatedAccount;
        }

        const investmentsSources = LocalAccountsState.map(mapAccountToInvestmentSource);

        // No-op guard: compare the upserted accounts against the last-persisted `investmentsSources`
        // (normalized through the same projection) and short-circuit with `false` when nothing
        // changed, so callers can avoid re-triggering the plan on a no-change save.
        const persistedInvestmentsSources: Partial<InvestmentSourcesType>[] =
            adviceClient?.accounts?.investmentsSources ?? [];
        const persistedProjection = persistedInvestmentsSources
            .map(mapInvestmentSourceToAccount)
            .map(mapAccountToInvestmentSource);
        if (isEqual(investmentsSources, persistedProjection)) {
            return false;
        }

        const payload = {
            ...adviceClient,
            accounts: {
                ...adviceClient.accounts,
                investmentsSources
            }
        };

        await dispatch(saveClient(payload));

        // `saveClient` syncs the response into `adviceClient`; refill the inline table from the
        // freshly persisted `investmentsSources`.
        const responseInvestmentsSources: Partial<InvestmentSourcesType>[] =
            getState().adviceClient?.accounts?.investmentsSources ?? [];
        dispatch(setClientAccounts(responseInvestmentsSources.map(mapInvestmentSourceToAccount)));
        return true;
    };

// Hydrate the inline-table slice on first load by copying
// `adviceClient.accounts.investmentsSources` into `adviceClientAccounts.accounts`, or
// falling back to the empty initial state when nothing has been persisted yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hydrateClientAccounts = () => (dispatch: any, getState: any) => {
    const persistedInvestmentsSources: Partial<InvestmentSourcesType>[] =
        getState().adviceClient?.accounts?.investmentsSources ?? [];
    if (persistedInvestmentsSources.length > 0) {
        dispatch(setClientAccounts(persistedInvestmentsSources.map(mapInvestmentSourceToAccount)));
    } else {
        dispatch(resetClientAccounts());
    }
};

export type { AccountContribution, ClientAccountEntry };

export default clientAccounts.reducer;
